import React, { useState } from 'react'
import CustomAlert from '../../shared/CustomAlert'
import { observer } from 'mobx-react-lite'
import { MDBCollapse } from 'mdb-react-ui-kit'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import CorpEmailSubscriber from './CorpEmailSubscriber.component'
import ContentHtml from './../../content/ContentHtml/ContentHtml.component'
import ConfirmDeleteModalComponent from './../../adminShared/ConfirmDeleteModal/ConfirmDeleteModal.component'

import './CorpEmailManager.component.scss'

const CorpEmailManager = ({ User }) => {
  const [err, setErr] = useState(null),
    [corpEmail, setCorpEmail] = useState(null),
    [isSubscribing, setIsSubscribing] = useState(false),
    [isUnsubscribing, setIsUnsubscribing] = useState(false),
    [hasPurchased, setHasPurchased] = useState(false),
    [hasUnsubscribed, setHasUnsubscribed] = useState(false),
    [confirmUnsubscribe, setConfirmUnsubscribe] = useState(false),
    [passwordWasGenerated, setPasswordWasGenerated] = useState(false)

  const subscribe = async () => {
    if (UserProfileService.isA(['affiliate-group', 'associate-group']))
      return setErr('You must be a Signature Agent or Higher to request your USABG corporate email.')

    setIsSubscribing(true)

    try {
      let ce = await UserService.subscribeCorporateEmail(UserProfileService.getUserId(), { should_notify: true })
      UserProfileService.set('u_company_email', ce?.email || UserProfileService.get('u_company_email'))
      User.set('u_company_email', ce?.email || UserProfileService.get('u_company_email'))
      setErr(null)
      setCorpEmail(ce?.email)
      setHasPurchased(true)

      // Check if password was generated
      await checkIfPasswordWasGenerated()
    } catch (ex) {
      setErr(`${ex}`)
      setCorpEmail(null)
      setHasPurchased(null)
      setPasswordWasGenerated(false)
    }

    setIsSubscribing(false)
  }

  const obtain = async () => {
    if (UserProfileService.isA(['affiliate-group', 'associate-group']))
      return setErr('You must be a Signature Agent or Higher to request your USABG corporate email.')

    setIsSubscribing(true)

    try {
      let ce = await UserService.subscribeCorporateEmail(UserProfileService.getUserId(), {
        should_notify: true,
        no_charge: true,
      })
      UserProfileService.set('u_company_email', ce?.email || UserProfileService.get('u_company_email'))
      User.set('u_company_email', ce?.email || UserProfileService.get('u_company_email'))
      setErr(null)
      setCorpEmail(ce?.email)
      setHasPurchased(true)

      // Check if password was generated
      await checkIfPasswordWasGenerated()
    } catch (ex) {
      setErr(`${ex}`)
      setCorpEmail(null)
      setHasPurchased(null)
      setPasswordWasGenerated(false)
    }

    setIsSubscribing(false)
  }

  const checkIfPasswordWasGenerated = async () => {
    try {
      const passwordMeta = await UserService.getUserMetas(
        'office365---password-generated',
        UserProfileService.getUserId()
      )
      setPasswordWasGenerated(passwordMeta && passwordMeta.length > 0 && passwordMeta[0]?.meta_value)
    } catch (ex) {
      // If we can't check, assume false
      setPasswordWasGenerated(false)
    }
  }

  const requestUnsubscribe = UserCorpEmail => {
    let msg = 'USABG Corporate Email'

    if (UserCorpEmail && UserCorpEmail.get('email')) msg += ` '${UserCorpEmail.get('email')}'`
    msg += '. This will cancel your email subscription & delete the inbox. This is permanent!'

    setConfirmUnsubscribe(msg)
  }

  return (
    <div id='CorpEmailManagerComponent'>
      <div className='corp-email-details'>
        <ContentHtml contentSlug='purchase-corporate-email' />
      </div>
      <div className='payment-details'>
        <CorpEmailSubscriber
          User={User}
          hasPurchased={hasPurchased}
          hasUnsubscribed={hasUnsubscribed}
          isSubscribing={isSubscribing}
          onSubscribe={subscribe}
          onObtain={obtain}
          isUnsubscribing={isUnsubscribing}
          onUnsubscribe={requestUnsubscribe}
        />
        <MDBCollapse open={!!err}>
          <CustomAlert color='danger' style={{ textAlign: 'center' }}>
            {err}
          </CustomAlert>
        </MDBCollapse>
        <MDBCollapse open={!!corpEmail}>
          <br />
          <CustomAlert color='success'>
            <strong>Great news! Your corporate email has been setup as {corpEmail}.</strong>
            <br />
            <br />
            Setup instructions will be emailed to you at {UserProfileService.get('u_email')}
            {passwordWasGenerated && (
              <>
                <br />
                <br />
                <strong style={{ color: '#d32f2f' }}>
                  ⚠️ IMPORTANT: A randomly generated password was created for your Office365 account. You MUST read the
                  email we sent you to get your initial password. You will be required to change this password on your
                  first login.
                </strong>
              </>
            )}
          </CustomAlert>
        </MDBCollapse>
        <MDBCollapse open={!!hasUnsubscribed}>
          <br />
          <CustomAlert color='success'>
            <strong>Your corporate email subscription has been canceled.</strong>
          </CustomAlert>
        </MDBCollapse>
      </div>

      <ConfirmDeleteModalComponent
        confirm={confirmUnsubscribe}
        shouldEnable={true}
        onConfirm={async () => {
          setIsUnsubscribing(true)
          setConfirmUnsubscribe(false)

          try {
            await UserService.deleteCorporateEmail(UserProfileService.getUserId())

            if (
              UserProfileService.get('u_company_email') &&
              (UserProfileService.get('u_company_email').includes('usabg.com') ||
                UserProfileService.get('u_company_email').includes('usabg.net'))
            )
              UserProfileService.set('u_company_email', '')

            setErr(null)
            setCorpEmail(null)
            setHasPurchased(null)
            setHasUnsubscribed(true)
          } catch (ex) {
            setErr(`${ex}`)
            setCorpEmail(null)
            setHasPurchased(null)
          }

          setIsUnsubscribing(false)
          setHasUnsubscribed(true)
        }}
        onCancel={() => setConfirmUnsubscribe(false)}
      />
    </div>
  )
}

export default observer(CorpEmailManager)
